import axios from 'axios'
import { aiCoreService } from './ai-core-service'

class AiAnalyticsService {
  private apiClient = axios.create({
    baseURL: '/api/ai/analytics',
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json'
    }
  })

  async getAnalyticsOverview() {
    try {
      const response = await this.apiClient.get('/overview')
      return response.data
    } catch (error) {
      console.error('获取分析概览失败:', error)
      return { stats: [], charts: {} }
    }
  }

  async getUserBehaviorAnalysis() {
    try {
      const response = await this.apiClient.get('/user-behavior')
      return response.data
    } catch (error) {
      console.error('获取用户行为分析失败:', error)
      return { activeUsers: 0, newUsers: 0, userRetention: 0, averageSessionDuration: '0:00', topPages: [] }
    }
  }

  async getBusinessAnalysis() {
    try {
      const response = await this.apiClient.get('/business')
      return response.data
    } catch (error) {
      console.error('获取业务分析失败:', error)
      return { totalSales: 0, orderCount: 0, averageOrderValue: 0, salesGrowth: 0, topServices: [] }
    }
  }

  async getModelPerformance() {
    try {
      const response = await this.apiClient.get('/model-performance')
      return response.data
    } catch (error) {
      console.error('获取模型性能分析失败:', error)
      return { accuracy: 0, recall: 0, responseTime: 0, f1Score: 0, performanceTrend: {}, modelDistribution: {} }
    }
  }

  async getPredictionAnalysis() {
    try {
      const response = await this.apiClient.get('/prediction')
      return response.data
    } catch (error) {
      console.error('获取预测分析失败:', error)
      return { salesPrediction: { labels: [], data: [] }, userGrowthPrediction: { labels: [], data: [] } }
    }
  }

  async getTrendAnalysis() {
    try {
      const response = await this.apiClient.get('/trend')
      return response.data
    } catch (error) {
      console.error('获取趋势分析失败:', error)
      return { userGrowth: 0, salesGrowth: 0, serviceDemand: 0, seasonalTrends: {} }
    }
  }

  async getAnalysisReports() {
    try {
      const response = await this.apiClient.get('/reports')
      return response.data
    } catch (error) {
      console.error('获取分析报告失败:', error)
      return []
    }
  }

  async getAIInsights() {
    try {
      const response = await this.apiClient.get('/insights')
      return response.data
    } catch (error) {
      console.error('获取智能洞察失败:', error)
      return []
    }
  }

  async exportAnalyticsReport(reportId: string, format: string = 'pdf') {
    try {
      const response = await this.apiClient.get(`/reports/${reportId}/export`, {
        params: { format },
        responseType: 'blob'
      })
      return response.data
    } catch (error) {
      console.error('导出分析报告失败:', error)
      throw error
    }
  }

  async getRealTimeAnalysis() {
    try {
      const response = await this.apiClient.get('/realtime')
      return response.data
    } catch (error) {
      console.error('获取实时分析失败:', error)
      return { currentUsers: 0, activeSessions: 0, pageViewsPerMinute: 0, conversionRate: 0, revenuePerHour: 0, recentActivities: [], performanceMetrics: {} }
    }
  }

  async getMultiDimensionAnalysis(dimensions: string[], metrics: string[]) {
    try {
      const response = await this.apiClient.post('/multi-dimension', {
        dimensions,
        metrics
      })
      return response.data
    } catch (error) {
      console.error('获取多维度分析失败:', error)
      return { dimensions: [], metrics: [], data: [], summary: { total: 0, average: 0, growth: 0 } }
    }
  }

  async getIndustryComparison() {
    try {
      const response = await this.apiClient.get('/industry-comparison')
      return response.data
    } catch (error) {
      console.error('获取行业对比分析失败:', error)
      return { metrics: [], ourValues: [], industryAverage: [], topCompetitor: [], comparison: {} }
    }
  }

  async getUserSegmentation() {
    try {
      const response = await this.apiClient.get('/user-segmentation')
      return response.data
    } catch (error) {
      console.error('获取用户细分分析失败:', error)
      return { segments: [], segmentBehavior: {} }
    }
  }

  async getConversionFunnel() {
    try {
      const response = await this.apiClient.get('/conversion-funnel')
      return response.data
    } catch (error) {
      console.error('获取转化率漏斗分析失败:', error)
      return { stages: [], dropOff: [], insights: [] }
    }
  }

  async getCustomerLifetimeValue() {
    try {
      const response = await this.apiClient.get('/customer-ltv')
      return response.data
    } catch (error) {
      console.error('获取客户生命周期价值分析失败:', error)
      return { averageLTV: 0, ltvBySegment: [], ltvTrend: {}, retentionAnalysis: {} }
    }
  }

  async getEnhancedPredictionAnalysis(period: string = '3months') {
    try {
      const response = await this.apiClient.get('/prediction/enhanced', {
        params: { period }
      })
      return response.data
    } catch (error) {
      console.error('获取增强预测分析失败:', error)
      return { salesPrediction: {}, userGrowthPrediction: {}, demandForecast: {}, riskAssessment: {} }
    }
  }

  async getEnhancedAIInsights() {
    try {
      const response = await this.apiClient.get('/insights/enhanced')
      return response.data
    } catch (error) {
      console.error('获取增强智能洞察失败:', error)
      return []
    }
  }

  async generateCustomReport(reportConfig: any) {
    try {
      const response = await this.apiClient.post('/reports/custom', reportConfig)
      return response.data
    } catch (error) {
      console.error('生成自定义报告失败:', error)
      throw error
    }
  }

  async getRealTimeAnomalyDetection() {
    try {
      const response = await this.apiClient.get('/anomaly-detection/realtime')
      return response.data
    } catch (error) {
      console.error('获取实时异常检测失败:', error)
      return { status: 'normal', lastChecked: new Date().toISOString(), anomalies: [], monitoredMetrics: [] }
    }
  }

  async getTextAnalysis(text: string) {
    try {
      const response = await this.apiClient.post('/nlp/analyze', { text })
      return response.data
    } catch (error) {
      console.error('文本分析失败:', error)
      return { sentiment: '', entities: [], keywords: [], intent: '', summary: '', confidence: 0, language: '', model: '' }
    }
  }

  async getImageAnalysis(imageUrl: string) {
    try {
      const response = await this.apiClient.post('/vision/analyze', { imageUrl })
      return response.data
    } catch (error) {
      console.error('图像分析失败:', error)
      return { objects: [], classification: '', confidence: 0, model: '' }
    }
  }

  async getMultiVariablePrediction(variables: string[], period: string = '3months') {
    try {
      const response = await this.apiClient.post('/prediction/multivariable', { variables, period })
      return response.data
    } catch (error) {
      console.error('多变量预测分析失败:', error)
      return { variables: [], period: '', predictions: [], correlations: [] }
    }
  }

  async getTimeSeriesDecomposition(data: number[], frequency: number = 12) {
    try {
      const response = await this.apiClient.post('/analytics/time-series/decompose', { data, frequency })
      return response.data
    } catch (error) {
      console.error('时间序列分解失败:', error)
      return { observed: [], trend: [], seasonal: [], residual: [], frequency: 0, model: '' }
    }
  }

  async getStreamingAnalysis(windowSize: number = 60) {
    try {
      const response = await this.apiClient.get('/analytics/streaming', { params: { windowSize } })
      return response.data
    } catch (error) {
      console.error('流式数据分析失败:', error)
      return { windowSize: 0, currentWindow: [], movingAverage: [], trend: '', anomalies: [], throughput: 0 }
    }
  }

  async getIndustrySpecificAnalysis(industry: string) {
    try {
      const response = await this.apiClient.get('/analytics/industry-specific', { params: { industry } })
      return response.data
    } catch (error) {
      console.error('行业特定分析失败:', error)
      return { industry: '', benchmarks: {}, performance: {}, recommendations: [], competitiveAnalysis: {} }
    }
  }

  async getIntelligentVisualizationConfig(dataType: string, dimensions: string[]) {
    try {
      const response = await this.apiClient.post('/analytics/visualization/config', { dataType, dimensions })
      return response.data
    } catch (error) {
      console.error('智能可视化配置失败:', error)
      return { dataType: '', dimensions: [], recommendedChartType: 'bar', configuration: {}, insights: [] }
    }
  }

  async evaluatePredictionModel(modelId: string, testData: any[]) {
    try {
      const response = await this.apiClient.post('/models/evaluate', { modelId, testData })
      return response.data
    } catch (error) {
      console.error('模型评估失败:', error)
      return { modelId: '', modelName: '', metrics: {}, testDataSize: 0, executionTime: 0, recommendations: [], comparison: {} }
    }
  }
}

export const aiAnalyticsService = new AiAnalyticsService()